import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL } from '../../config';
import Sidebar from '../Shared/Sidebar';
import Header from '../Shared/Header';
import './ReportAnalyzer.css';

const ReportAnalyzer = () => {
    const { t } = useTranslation(['reports', 'common', 'navigation']);
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('analysis');
    const [attempts, setAttempts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showExceededModal, setShowExceededModal] = useState(false);
    const [pendingRetake, setPendingRetake] = useState(null);
    const userId = localStorage.getItem('userId');
    const username = localStorage.getItem('username') || 'Student';
    const studentClass = localStorage.getItem('studentClass') || '10';

    useEffect(() => {
        const fetchAttempts = async () => {
            if (!userId) {
                setLoading(false);
                return;
            }
            try {
                const res = await fetch(`${API_BASE_URL}get_test_attempts.php?user_id=${userId}`, { credentials: 'include' });
                const data = await res.json();
                if (data.status === 'success') {
                    setAttempts(data.attempts);
                }
            } catch (err) {
                console.error("Error fetching test attempts:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchAttempts();
    }, [userId]);

    const handleRetake = async (attempt) => {
        try {
            const chapterId = attempt.chapter_id || 0;
            const res = await fetch(`${API_BASE_URL}check_package_status.php?user_id=${userId}&chapter_id=${chapterId}`, { credentials: 'include' });
            const data = await res.json();

            if (data.status === 'success') {
                if (data.exceeded && data.purchase_status !== 'active') {
                    // Quota exceeded AND no active subscription → show upgrade modal
                    setShowExceededModal(true);
                    setPendingRetake(attempt);
                } else {
                    navigate('/loader', {
                        state: {
                            board: localStorage.getItem('boardName'),
                            class: localStorage.getItem('studentClass'),
                            subject: attempt.subject_name,
                            subject_id: attempt.subject_id,
                            chapter: attempt.chapter_name || 'General',
                            chapter_id: attempt.chapter_id
                        }
                    });
                }
            } else {
                alert(t('reports:reports.analysisReport.alerts.verifyError', "Unable to verify account status. Please try again."));
            }
        } catch (err) {
            console.error("Error checking package status:", err);
            alert(t('reports:reports.analysisReport.alerts.connError', "Connection error. Please try again."));
        }
    };


    const chapterPerformance = [
        { name: 'Algebra', score: 40, color: '#ef4444', bg: '#fee2e2' },
        { name: 'Geometry', score: 90, color: '#10b981', bg: '#d1fae5' },
        { name: 'Science', score: 75, color: '#f97316', bg: '#ffedd5' },
        { name: 'English', score: 80, color: '#ef4444', bg: '#fee2e2' },
    ];

    const weakTopics = (() => {
        if (attempts.length === 0) return [];

        // Find areas where score is below 60%
        const lowScoreAttempts = attempts.filter(att => parseFloat(att.score_percentage) < 60);

        // Take unique subject/chapter combinations
        const uniqueWeak = [];
        const seen = new Set();

        for (const att of lowScoreAttempts) {
            const name = att.chapter_name || att.subject_name;
            if (!seen.has(name)) {
                seen.add(name);
                uniqueWeak.push({
                    name: name,
                    score: Math.round(att.score_percentage),
                    color: '#ef4444'
                });
            }
            if (uniqueWeak.length >= 3) break;
        }
        return uniqueWeak;
    })();


    const overallScore = attempts.length > 0
        ? Math.round(attempts.reduce((sum, att) => sum + parseFloat(att.score_percentage), 0) / attempts.length)
        : 0;
    const circumference = 2 * Math.PI * 54;
    const strokeOffset = circumference - (overallScore / 100) * circumference;

    return (
        <div className="dashboard-wrapper">
            <div className="dashboard-container">
                {/* Shared Sidebar */}
                <Sidebar active="analysis" />

                {/* Main Content */}
                <main className="main-content-area">
                    <Header
                        title={`📊 ${t('reports:reports.analysisReport.title', 'Analysis Report')}`}
                        subtitle={t('reports:reports.analysisReport.subtitle', { class: studentClass, name: username, defaultValue: `Performance breakdown · Class ${studentClass} · ${username}` })}
                        showActions={true}
                        hidePill={true}
                    />

                    <div className="dashboard-scroll-container">
                        <div className="ra-content-card">
                            {/* Tabs */}
                            <div className="ra-tabs">
                                <button className={`ra-tab ${activeTab === 'analysis' ? 'active' : ''}`} onClick={() => setActiveTab('analysis')}>
                                    📈 {t('reports:reports.analysisReport.tab', 'Analysis Report')}
                                </button>
                            </div>

                            {/* Main Grid */}
                            <div className="ra-grid">

                                {/* Left — Score + Chapter Performance */}
                                <div className="ra-left-panel">

                                    {/* Score Card */}
                                    {loading ? (
                                        <div className="ra-score-card shimmer">
                                            <div className="ra-shimmer-placeholder gauge"></div>
                                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                <div className="ra-shimmer-placeholder label"></div>
                                                <div className="ra-shimmer-placeholder value"></div>
                                            </div>
                                            <div className="ra-shimmer-placeholder trophy"></div>
                                        </div>
                                    ) : (
                                        <div className="ra-score-card">
                                            <div className="ra-score-gauge">
                                                <svg viewBox="0 0 120 120" width="140" height="140">
                                                    <circle cx="60" cy="60" r="54" fill="none" stroke="#e2e8f0" strokeWidth="10" />
                                                    <circle
                                                        cx="60" cy="60" r="54"
                                                        fill="none"
                                                        stroke="url(#scoreGrad)"
                                                        strokeWidth="10"
                                                        strokeLinecap="round"
                                                        strokeDasharray={circumference}
                                                        strokeDashoffset={strokeOffset}
                                                        transform="rotate(-90 60 60)"
                                                    />
                                                    <defs>
                                                        <linearGradient id="scoreGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                                                            <stop offset="0%" stopColor="#3b82f6" />
                                                            <stop offset="100%" stopColor="#8b5cf6" />
                                                        </linearGradient>
                                                    </defs>
                                                </svg>
                                                <div className="ra-score-center">
                                                    <span className="ra-score-label">{t('reports:reports.analysisReport.yourScore', 'Your Score')}</span>
                                                    <span className="ra-score-value">{overallScore}%</span>
                                                </div>
                                            </div>
                                            <div className="ra-trophy">🏆</div>
                                        </div>
                                    )}

                                    {/* Taken Tests / History */}
                                    {loading ? (
                                        <div className="ra-chapter-card shimmer">
                                            <div className="ra-shimmer-placeholder title-shimmer"></div>
                                            <div className="ra-attempts-list">
                                                {[1, 2, 3].map(i => <div key={i} className="ra-shimmer-placeholder row-item"></div>)}
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="ra-chapter-card">
                                            <h2 className="ra-section-title">{t('reports:reports.analysisReport.testHistory', 'Tests History')}</h2>
                                            {attempts.length === 0 ? (
                                                <div className="ra-empty">{t('reports:reports.analysisReport.noTests', 'No tests taken yet.')}</div>
                                            ) : (
                                                <div className="ra-attempts-list">
                                                    {attempts.map((attempt) => (
                                                        <div className="ra-attempt-row" key={attempt.id}>
                                                            <div className="ra-attempt-info">
                                                                <span className="ra-attempt-subject">{attempt.subject_name}</span>
                                                                <span className="ra-attempt-chapter">{attempt.chapter_name || t('reports:reports.analysisReport.generalTest', 'General Test')}</span>
                                                                <span className="ra-attempt-date">{new Date(attempt.submitted_at).toLocaleDateString()}</span>
                                                            </div>
                                                            <div className="ra-attempt-stats">
                                                                <span className="ra-attempt-score" style={{ color: attempt.score_percentage >= 70 ? '#10b981' : attempt.score_percentage >= 40 ? '#f97316' : '#ef4444' }}>
                                                                    {Math.round(attempt.score_percentage)}%
                                                                </span>
                                                                <button
                                                                    className="ra-analyze-btn"
                                                                    onClick={() => navigate('/ai-doctor-report', {
                                                                        state: {
                                                                            mode: 'review',
                                                                            test_id: attempt.id,
                                                                            score_percentage: attempt.score_percentage,
                                                                            correct_answers: attempt.correct_answers,
                                                                            total_questions: attempt.total_questions,
                                                                            subject: attempt.subject_name,
                                                                            chapter: attempt.chapter_name || 'General',
                                                                            subject_id: attempt.subject_id,
                                                                            chapter_id: attempt.chapter_id
                                                                        }
                                                                    })}
                                                                >
                                                                    {t('reports:reports.analysisReport.analyze', 'Analyze')}
                                                                </button>
                                                                <button
                                                                    className="ra-retake-btn"
                                                                    onClick={() => handleRetake(attempt)}
                                                                >
                                                                    {t('reports:reports.analysisReport.retake', 'Retake')}
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    {/* Weak Topics */}
                                    {weakTopics.length > 0 && (
                                        <div className="ra-weak-topics-card">
                                            <h2 className="ra-section-title">{t('reports:reports.analysisReport.weakTopics', 'Weak Topics')}</h2>
                                            <div className="ra-weak-row">
                                                {weakTopics.map((t_topic, i) => (
                                                    <div className="ra-weak-chip" key={i}>
                                                        <span className="ra-weak-name">{t_topic.name}</span>
                                                        <span className="ra-weak-pct" style={{ color: t_topic.color }}>{t_topic.score}%</span>
                                                        <div className="ra-weak-bar">
                                                            <div style={{ width: `${t_topic.score}%`, height: '100%', background: t_topic.color, borderRadius: '4px' }}></div>
                                                        </div>
                                                    </div>
                                                ))}
                                                <button className="ra-practice-btn-main" onClick={() => navigate('/subjects')}>{t('reports:reports.analysisReport.practice', 'Practice')}</button>
                                            </div>
                                        </div>
                                    )}
                                </div>


                            </div>
                        </div>
                    </div>
                </main>
            </div>

            {/* Premium Upsell Modal */}
            {showExceededModal && (
                <div className="dr-modal-overlay">
                    <div className="dr-upsell-modal">
                        <div className="dr-modal-header">
                            <div className="dr-modal-icon">💎</div>
                            <h2>{t('reports:reports.analysisReport.upsell.title', 'Chapter Limit Reached')}</h2>
                            <button className="dr-modal-close" onClick={() => setShowExceededModal(false)}>✕</button>
                        </div>
                        <div className="dr-modal-body">
                            <p>{t('reports:reports.analysisReport.upsell.description', "You've utilized your free unique chapter quota. To continue re-taking tests and unlocking more chapters, upgrade to our Chapter Pack.")}</p>
                            <div className="dr-offer-card">
                                <div className="dr-offer-info">
                                    <span className="dr-offer-title">{t('reports:reports.analysisReport.upsell.offer', { count: 8, defaultValue: '8 Premium Chapters' })}</span>
                                    <span className="dr-offer-price">{t('reports:reports.analysisReport.upsell.price', { amount: 20, defaultValue: 'Only ₹20' })}</span>
                                </div>
                                <div className="dr-offer-check">✓</div>
                            </div>
                            <ul className="dr-modal-features">
                                {(t('reports:reports.analysisReport.upsell.features', { returnObjects: true, count: 8 }) || []).map((feature, idx) => (
                                    <li key={idx}>✨ {feature}</li>
                                ))}
                            </ul>
                        </div>
                        <div className="dr-modal-footer">
                            <button className="dr-btn-primary full-width" style={{ background: 'linear-gradient(135deg, #00f2ff 0%, #3b82f6 100%)', color: '#000', border: 'none', padding: '12px', borderRadius: '12px', fontWeight: 'bold', cursor: 'pointer' }} onClick={() => navigate('/pricing')}>
                                {t('reports:reports.analysisReport.upsell.buyBtn', 'Buy Chapter Pack Now')}
                            </button>
                            <button className="dr-link-btn" style={{ background: 'none', border: 'none', color: '#64748b', textDecoration: 'underline', marginTop: '10px', cursor: 'pointer' }} onClick={() => setShowExceededModal(false)}>
                                {t('reports:reports.analysisReport.upsell.maybeLater', 'Maybe later')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ReportAnalyzer;
